/**
 * Voice Input System
 * Push-to-talk, voice commands, and TTS feedback
 */

import { store } from '../lib/appState.js';

class VoiceSystem {
  constructor() {
    this.recognition = null;
    this.synthesis = window.speechSynthesis || null;
    this.isListening = false;
    this.listeners = new Set();
    this.commands = new Map();
    this.initRecognition();
    this.registerDefaultCommands();
  }

  initRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    this.recognition.onresult = (event) => {
      const result = event.results[event.results.length - 1];
      const transcript = result[0].transcript.trim();
      const isFinal = result.isFinal;

      this.notify({ type: 'transcript', transcript, isFinal });

      if (isFinal) {
        const command = this.matchCommand(transcript);
        if (command) {
          command.handler(transcript);
          this.notify({ type: 'command', name: command.name, transcript });
        } else {
          this.notify({ type: 'input', transcript });
        }
      }
    };

    this.recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        this.notify({ type: 'error', error: event.error });
      }
      this.isListening = false;
      this.notify({ type: 'status', listening: false });
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.notify({ type: 'status', listening: false });
    };
  }

  registerDefaultCommands() {
    this.registerCommand('new chat', /^(new chat|start new|new conversation)/i, () => {
      store.setState({ ui: { sidebarOpen: false } });
      // Trigger through store action
      this.notify({ type: 'action', action: 'new-chat' });
    });

    this.registerCommand('parallel mode', /^(parallel mode|enable parallel|toggle parallel)/i, () => {
      this.notify({ type: 'action', action: 'toggle-parallel' });
    });

    this.registerCommand('send to canvas', /^(send to canvas|create canvas|canvas)/i, () => {
      this.notify({ type: 'action', action: 'send-to-canvas' });
    });

    this.registerCommand('switch to', /^switch to (\w+)/i, (transcript) => {
      const match = transcript.match(/switch to (\w+)/i);
      if (match) {
        this.notify({ type: 'action', action: 'switch-model', model: match[1].toLowerCase() });
      }
    });

    this.registerCommand('stop', /^(stop|cancel|abort)/i, () => {
      this.notify({ type: 'action', action: 'stop' });
    });

    this.registerCommand('clear', /^(clear|clear chat|clear conversation)/i, () => {
      this.notify({ type: 'action', action: 'clear' });
    });

    this.registerCommand('settings', /^(settings|open settings|preferences)/i, () => {
      this.notify({ type: 'action', action: 'settings' });
    });

    this.registerCommand('help', /^(help|voice help|what can you do)/i, () => {
      this.speak('Available commands: new chat, parallel mode, switch to a model, send to canvas, stop, clear, settings, and help.');
      this.notify({ type: 'action', action: 'help' });
    });

    this.registerCommand('read response', /^(read|read response|read aloud|read it)/i, () => {
      this.notify({ type: 'action', action: 'read-response' });
    });

    this.registerCommand('scroll', /^(scroll down|scroll up|go down|go up)/i, (transcript) => {
      const direction = /up/i.test(transcript) ? 'up' : 'down';
      this.notify({ type: 'action', action: 'scroll', direction });
    });
  }

  registerCommand(name, pattern, handler) {
    this.commands.set(name, { name, pattern, handler });
  }

  matchCommand(transcript) {
    for (const [, command] of this.commands) {
      if (command.pattern.test(transcript)) {
        return command;
      }
    }
    return null;
  }

  /**
   * Start listening (push-to-talk)
   */
  start() {
    if (!this.recognition || this.isListening) return false;
    try {
      this.recognition.start();
      this.isListening = true;
      this.notify({ type: 'status', listening: true });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Stop listening
   */
  stop() {
    if (!this.recognition || !this.isListening) return;
    this.recognition.stop();
    this.isListening = false;
    this.notify({ type: 'status', listening: false });
  }

  /**
   * Text-to-speech
   */
  speak(text) {
    if (!this.synthesis) return;
    this.synthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    this.synthesis.speak(utterance);
  }

  /**
   * Stop speaking
   */
  stopSpeaking() {
    if (this.synthesis) this.synthesis.cancel();
  }

  /**
   * Check if speech recognition is supported
   */
  isSupported() {
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  /**
   * Subscribe to voice events
   */
  onEvent(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify(event) {
    this.listeners.forEach(cb => cb(event));
  }
}

export const voice = new VoiceSystem();
